/**
 * ReadinessResults — ISO 27001 Readiness Exam Results & Backlog
 */

import React, { useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { readinessApi } from '../services/api';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';

type SectionScore = {
  section: string;
  raw: number;
  max: number;
  percentage: number;
};

type BacklogItem = {
  backlogId: string;
  questionId: string;
  clause: string;
  section: string;
  title: string;
  priority: 'Critical' | 'High' | 'Medium';
  currentScore: number;
  maxScore: number;
  comment: string;
};

const STAGE_COLOURS: Record<string, string> = {
  initial: 'danger',
  emerging: 'warning',
  structured: 'info',
  audit_ready: 'success',
};

const PRIORITY_COLOURS: Record<string, string> = {
  Critical: 'danger',
  High: 'warning',
  Medium: 'info',
};


export const ReadinessResults: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'backlog'>('overview');
  const [filterPriority, setFilterPriority] = useState<string>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['readiness-assessment', assessmentId],
    queryFn: () => readinessApi.get(assessmentId!),
    enabled: !!assessmentId,
  });

  const assessment = data?.assessment;

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Assessment not found or could not be loaded.</div>
      </div>
    );
  }

  if (assessment.status !== 'completed') {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <span className="np-icon-badge-lg np-tone-warning mx-auto mb-3">
          <i className="ri-draft-line"></i>
        </span>
        <h5>This assessment has not been submitted yet.</h5>
        <a href={`/readiness/${assessmentId}`} className="np-btn np-btn-primary mt-2">
          <i className="ri-arrow-right-line"></i>Continue Assessment
        </a>
      </div>
    );
  }

  const stage = assessment.stage as string;
  const stageColour = STAGE_COLOURS[stage] || 'neutral';
  const score = Number(assessment.normalisedScore ?? 0);

  const sectionScores: SectionScore[] = Object.values(assessment.sectionScores || {}) as SectionScore[];
  const backlog: BacklogItem[] = (assessment.backlogItems || []) as BacklogItem[];
  const ceilingApplied: string[] = assessment.ceilingApplied || [];

  const filteredBacklog =
    filterPriority === 'all' ? backlog : backlog.filter((b) => b.priority === filterPriority);

  const criticalCount = backlog.filter((b) => b.priority === 'Critical').length;
  const highCount = backlog.filter((b) => b.priority === 'High').length;
  const mediumCount = backlog.filter((b) => b.priority === 'Medium').length;

  const submittedDate = assessment.submittedAt
    ? new Date(assessment.submittedAt).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '—';

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      <PageHeader
        icon="ri-award-line"
        title="Readiness Results"
        breadcrumbs={[{ label: 'ISO 27001 Readiness', to: '/readiness' }, { label: 'Results' }]}
      />

      {/* Score Hero */}
      <div className="np-card mb-4 overflow-hidden">
        <div className="np-card-body p-0">
          <div className="row g-0">
            {/* Colour bar */}
            <div className={`col-auto d-flex align-items-center justify-content-center bg-${stageColour} text-white px-4`} style={{ minWidth: 120 }}>
              <div className="text-center">
                <div className="fw-bold np-tabular" style={{ fontSize: '3rem', lineHeight: 1 }}>{score.toFixed(0)}</div>
                <div className="small opacity-75">/ 100</div>
                <div className="small fw-semibold mt-1 opacity-90">
                  {score >= 75 ? <i className="ri-shield-check-fill ri-lg"></i>
                   : score >= 50 ? <i className="ri-shield-line ri-lg"></i>
                   : score >= 25 ? <i className="ri-shield-flash-line ri-lg"></i>
                   : <i className="ri-shield-cross-line ri-lg"></i>}
                </div>
              </div>
            </div>
            <div className="col p-4">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                <div>
                  <h4 className="fw-bold mb-1">{assessment.title}</h4>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <span className={`np-chip np-tone-${stageColour} fs-6`}>
                      <i className="ri-award-line"></i>{assessment.stageName}
                    </span>
                    <span className="np-sub"><i className="ri-calendar-line me-1"></i>Submitted {submittedDate}</span>
                  </div>
                  <p className="np-sub mb-0">{assessment.stageDescription}</p>
                </div>
                <div className="text-end d-flex gap-2 flex-wrap justify-content-end">
                  <NavLink to="/readiness" className="np-btn np-btn-ghost np-btn-sm">
                    <i className="ri-arrow-left-line"></i>Back
                  </NavLink>
                  {backlog.length > 0 && (() => {
                    const uniqueClauses = [...new Set(backlog.map((b) => b.clause))].join(',');
                    return (
                      <NavLink
                        to={`/isms-journey?from=assessment&gaps=${encodeURIComponent(uniqueClauses)}`}
                        className="np-btn np-btn-primary np-btn-sm"
                      >
                        <i className="ri-road-map-line"></i>View Remediation Plan
                      </NavLink>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="np-grid np-grid-4 mb-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-danger"><i className="ri-error-warning-line"></i></span>
            <p className="np-stat-label">Critical Gaps</p>
            <p className="np-stat-value np-tabular np-tone-danger">{criticalCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">High Priority</p>
            <p className="np-stat-value np-tabular np-tone-warning">{highCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-info"><i className="ri-information-line"></i></span>
            <p className="np-stat-label">Medium Priority</p>
            <p className="np-stat-value np-tabular np-tone-info">{mediumCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className={`np-stat-icon np-tone-${stageColour}`}><i className="ri-bar-chart-2-line"></i></span>
            <p className="np-stat-label">Domains Assessed</p>
            <p className={`np-stat-value np-tabular np-tone-${stageColour}`}>{sectionScores.length}</p>
          </div>
        </div>
      </div>

      {/* Ceiling warnings */}
      {ceilingApplied.length > 0 && (
        <div className="np-banner np-tone-danger mb-4">
          <i className="ri-error-warning-fill"></i>
          <div>
            <strong>Stage ceiling applied</strong> — the following critical controls prevented a higher stage classification:
            <ul className="mt-2 mb-0 small">
              {ceilingApplied.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="np-tabs mb-4">
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'overview' ? ' active' : ''}`} onClick={() => setActiveTab('overview')}>
          <i className="ri-bar-chart-grouped-line"></i>Section Breakdown
        </button>
        <button type="button" className={`np-tab d-flex align-items-center gap-1${activeTab === 'backlog' ? ' active' : ''}`} onClick={() => setActiveTab('backlog')}>
          <i className="ri-task-line"></i>Remediation Backlog
          {backlog.length > 0 && <span className="np-chip np-tone-danger np-tabular" style={{ fontSize: '0.65rem' }}>{backlog.length}</span>}
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="np-card">
          <div className="np-card-head">
            <i className="ri-bar-chart-grouped-line text-primary"></i>
            <h2>Section Scores</h2>
          </div>
          <div className="np-card-body">
            {sectionScores
              .sort((a, b) => a.percentage - b.percentage)
              .map((sec) => {
                const colour = sec.percentage >= 75 ? 'success' : sec.percentage >= 50 ? 'info' : sec.percentage >= 25 ? 'warning' : 'danger';
                return (
                  <div key={sec.section} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-semibold">{sec.section}</span>
                      <span className={`np-chip np-tone-${colour} np-tabular`}>{sec.percentage.toFixed(0)}%</span>
                    </div>
                    <div className="np-progress">
                      <div
                        className={`np-fill np-tone-${colour}`}
                        role="progressbar"
                        style={{ width: `${sec.percentage}%` }}
                      />
                    </div>
                    <div className="np-hint np-tabular">
                      {sec.raw} / {sec.max} points
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* BACKLOG TAB */}
      {activeTab === 'backlog' && (
        <div className="np-card">
          <div className="np-card-head flex-wrap">
            <i className="ri-task-line text-primary"></i>
            <h2>Remediation Backlog <span className="np-sub fw-normal">({backlog.length} items)</span></h2>
            <div className="np-pill-toggle-group">
              {(['all', 'Critical', 'High', 'Medium'] as const).map((p) => {
                const col = p === 'all' ? 'neutral' : PRIORITY_COLOURS[p];
                const isActive = filterPriority === p;
                return (
                  <button
                    key={p}
                    type="button"
                    className={`np-pill-toggle d-flex align-items-center gap-1 np-tone-${col}${isActive ? ' active' : ''}`}
                    onClick={() => setFilterPriority(p)}
                  >
                    {p === 'all' ? 'All' : <><i className="ri-circle-fill" style={{ fontSize: '0.5rem' }}></i>{p}</>}
                  </button>
                );
              })}
            </div>
          </div>

          {filteredBacklog.length === 0 ? (
            <div className="np-card-body text-center py-5 np-sub">
              <i className="ri-checkbox-circle-line ri-2x d-block mb-2 text-success"></i>
              No items match the selected filter.
            </div>
          ) : (
            <NimbusTable thead={<tr>
                <th>Priority</th>
                <th>Clause</th>
                <th>Requirement</th>
                <th className="text-center">Score</th>
                <th>Notes</th>
              </tr>}>
                  {filteredBacklog.map((item) => {
                    const colour = PRIORITY_COLOURS[item.priority] || 'neutral';
                    const pct = item.maxScore > 0 ? Math.round((item.currentScore / item.maxScore) * 100) : 0;
                    return (
                      <tr key={item.backlogId}>
                        <td>
                          <span className={`np-chip np-tone-${colour}`}>
                            {item.priority === 'Critical' && <i className="ri-error-warning-line"></i>}
                            {item.priority === 'High' && <i className="ri-alert-line"></i>}
                            {item.priority === 'Medium' && <i className="ri-information-line"></i>}
                            {item.priority}
                          </span>
                        </td>
                        <td>
                          <span className="np-chip np-tone-neutral">{item.clause}</span>
                        </td>
                        <td>
                          <div className="small fw-semibold">{item.title}</div>
                          <div className="np-hint">{item.section}</div>
                        </td>
                        <td className="text-center">
                          <div className="small np-hint mb-1 np-tabular">{item.currentScore} / {item.maxScore}</div>
                          <div className="np-progress mx-auto" style={{ height: 4, width: 60 }}>
                            <div className={`np-fill np-tone-${colour}`} style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                        <td>
                          {item.comment
                            ? <small className="np-hint fst-italic">"{item.comment}"</small>
                            : <small className="np-hint">—</small>}
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
        </div>
      )}

      <p className="np-sub mt-3">
        <i className="ri-information-line me-1"></i>
        Structural readiness indicator only — not a compliance certification. Engage a qualified Lead Implementer for definitive readiness assessment.
      </p>
    </div>
  );
};
